import { KatapultElement, html } from '../../mixins/katapult-element';
import { LazySet } from '../../modules/LazySet.js';
import { CamelCase } from '../../modules/CamelCase.js';
import { Path } from '../../modules/Path.js';
import { ToArray } from '../../modules/ToArray.js';
import { KatapultEditorTemplate } from './katapult-editor-template';
import { DefaultStyle } from './katapult-model-editor-style';

class KatapultClearanceEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        :host {
          padding: 0;
        }

        #threePanelLayout {
          display: flex;
          flex-grow: 1;
          overflow: hidden;
        }
        #threePanelLayout > div {
          display: flex;
          flex-direction: column;
          flex-basis: 0;
          flex-grow: 1;
          padding: 24px;
          overflow-y: auto;
        }
        #threePanelLayout > div:not(:last-child) {
          border-right: 1px solid rgba(0,0,0,0.06);
        }
      </style>

      <katapult-firebase-worker
        path="[[modelSpaceDetails.path]]/models/attributes"
        data="{{otherAttributes}}">
      </katapult-firebase-worker>
      <katapult-firebase-worker
        path="[[modelSpaceDetails.path]]/models/input_models"
        data="{{inputModels}}">
      </katapult-firebase-worker>
      <katapult-firebase-worker
        path="[[modelSpaceDetails.path]]/models/make_ready/make_ready_items"
        data="{{makeReadyItems}}">
      </katapult-firebase-worker>

      <paper-row style="border-radius: 16px 16px 0 0" grey divider>
        <paper-cell center>Rule Requirements</paper-cell>
        <paper-cell center>Pole Clearances</paper-cell>
        <paper-cell center>
          <paper-toggle-button checked="{{midspanClearances}}">Midspan Clearances</paper-toggle-button>
        </paper-cell>
      </paper-row>

      <!-- The clearance editor can slow the main thread if it is rendering a complicated clearance item. -->
      <template is="dom-if" if="[[renderEditor]]" restamp>
        <div id="threePanelLayout">
          <div>
            <template is="dom-repeat" items="[[steps]]" as="step">
              <katapult-editor-step creating-item="[[creatingItem]]" active="[[step.active]]" index="[[step.index]]" current-index="[[currentIndex]]" title="[[step.title]]">
                
                <template is="dom-if" if="[[equal(step.id, 'marker_type')]]" restamp>
                  <paper-table style="flex-grow: 1;" light-grey rounded outline slot="body">
                    <paper-row input>
                      <paper-cell>
                        <katapult-drop-down items="[[inputModels]]" label-function="[[inputModelLabelFunction]]" value-path="$key" label="Select a Marker Type" value="{{_itemData.marker_type}}" no-label-float></katapult-drop-down>
                      </paper-cell>
                    </paper-row>
                  </paper-table>
                </template>

                <template is="dom-if" if="[[equal(step.id, 'attributes')]]" restamp>
                  <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
                  <bind-path base="{{_itemData}}" path="[[step.path]]" value="{{step.$rules}}"></bind-path>
                  <attribute-table attributes="[[toAttributesArray(step.$rules.attributes)]]" slot="body" show-picklist-override other-attributes="[[filteredOtherAttr]]" on-attributes-changed="attributesChanged" logical max-depth="2" logical-operator="{{step.$rules.operator}}" model-space-details="[[modelSpaceDetails]]"></attribute-table>
                </template>

                <template is="dom-if" if="[[equal(step.id, 'exceptions')]]" restamp>
                  <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
                  <paper-table overflow-hidden rounded outline light-grey grow slot="body">
                    <paper-table-scroll on-sort-changed="exceptionsSortChanged">
                      <template is="dom-repeat" items="{{_itemData.exceptions}}">
                        <paper-row white divider slot="sortable">
                          <paper-cell grab icon>
                            <material-icon icon="drag_indicator" drag-handle></material-icon>
                          </paper-cell>
                          <paper-cell>
                            <paper-input id$="exception_input_[[index]]" label="Exception" value="{{item}}" no-label-float></paper-input>
                          </paper-cell>
                          <paper-cell icon-button>
                            <katapult-button iconOnly noBackground noBorder icon="delete" on-click="removeException"></katapult-button>
                          </paper-cell>
                        </paper-row>
                      </template>
                      <paper-row white ripple short slot="after" on-click="addException">
                        <paper-cell center pointer style="color: var(--paper-grey-600);">
                          <span>ADD EXCEPTION</span>
                          <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                        </paper-cell>
                      </paper-row>
                    </paper-table-scroll>
                  </paper-table>
                </template>                

              </katapult-editor-step>
            </template>
          </div>
          <div>
            <template is="dom-if" if="[[showClearances(creatingItem, finishedSteps)]]">
              <clearance-table clearances="{{_itemData.clearances}}" make-ready-items="[[makeReadyItems]]" item-key="[[itemKey]]"></clearance-table>
            </template>
            <div style="flex-grow: 1;"></div>
            ${super.createItemContainer}
          </div>
          <div>
            <template is="dom-if" if="[[showClearances(creatingItem, finishedSteps)]]"></template>
              <template is="dom-if" if="[[midspanClearances]]" restamp>
                <clearance-table clearances="{{_itemData.midspan_clearances}}" make-ready-items="[[makeReadyItems]]" item-key="[[itemKey]]"></clearance-table>          
              </template>
            </template>
          </div>
        </div>
      </template> 
    `;
  }

  static get properties() {
    return {
      clearancesRulesList: {
        type: Array,
        value: []
      },
      midspanClearances: {
        type: Boolean,
        value: false
      },
      filteredOtherAttr: {
        type: Object,
        value: {}
      }
    };
  }

  static get observers() {
    return [
      'updateMidspanClearances(_itemData.midspan_clearances)',
      'updateItemDataMidspanClearances(midspanClearances)',
      'updatedOtherAttributes(otherAttributes)'
    ];
  }

  ready() {
    super.ready();

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'marker_type',
        title: 'Marker Type',
        trigger: () => this.get('_itemData.marker_type') != null
      },
      {
        id: 'attributes',
        title: 'Required Marker Attributes',
        path: 'marker_rules'
      },
      {
        id: 'attributes',
        title: 'Required Trace Attributes',
        path: 'trace_rules'
      },
      {
        id: 'exceptions',
        title: 'Rule Exceptions'
      }
    ]);
  }

  _itemDataChanged(changeRecord, ...args) {
    super._itemDataChanged(changeRecord, ...args);
    // Stores the update for _itemData
    let propertiesUpdate = {};
    // Check for the trace attributes old rules format and update to new
    if (!changeRecord.base?.trace_rules && changeRecord.base?.trace_attributes) {
      propertiesUpdate['_itemData.trace_rules'] = {
        attributes: changeRecord.base?.trace_attributes,
        operator: 'all'
      };
      // Remove old data
      propertiesUpdate['_itemData.trace_attributes'] = null;
    }
    // Check for the marker attributes old rules format and update to new
    if (!changeRecord.base?.marker_rules && changeRecord.base?.attributes) {
      propertiesUpdate['_itemData.marker_rules'] = {
        attributes: changeRecord.base?.attributes,
        operator: 'all'
      };
      // Remove old data
      propertiesUpdate['_itemData.attributes'] = null;
    }
    // Update properties if we had changed
    if (Object.keys(propertiesUpdate).length) {
      this.setProperties(propertiesUpdate);
    }
  }

  addException() {
    if (!Array.isArray(this.get('_itemData.exceptions'))) this.set('_itemData.exceptions', []);
    this.push('_itemData.exceptions', '');
    setTimeout(() => {
      let input = this.shadowRoot.querySelector(`#exception_input_${this.get('_itemData.exceptions.length') - 1}`);
      if (input) {
        input.focus();
      }
    });
  }

  attributesChanged(e) {
    // Rebuild attributes on _itemData when it changes.
    let attributes = Path.copy(e.currentTarget.attributes);

    function attributesArrayToObject(attributes) {
      let groupIndex = 0;
      let temp = {};
      if (Array.isArray(attributes)) {
        for (let attr of attributes) {
          if (attr.group) {
            attr.attributes = attributesArrayToObject(attr.attributes);
            temp[`group_${groupIndex++}`] = Path.getSubset(attr, ['attributes', 'operator']);
          } else {
            temp[attr.attribute] = attr.value;
          }
        }
      }
      return temp;
    }
    // Initialize rules if missing.
    if (this.get(`_itemData.${e.model.step.path}`) == null) this.set(`_itemData.${e.model.step.path}`, {});
    // Set update attribute data.
    this.set(`_itemData.${e.model.step.path}.attributes`, attributesArrayToObject(attributes));
  }

  exceptionsSortChanged(e) {
    let removed = this.splice('_itemData.exceptions', e.detail.prevIndex, 1);
    this.splice('_itemData.exceptions', e.detail.index, 0, removed[0]);
  }

  async _saveUpdate(itemData, update, detail) {
    update = update || {};
    let highestPriorityValue = 0;

    let clearanceRules = await FirebaseWorker.ref(this.dataPath)
      .once('value')
      .then((s) => s.val());

    for (let clearanceType of ['clearances', 'midspan_clearances']) {
      for (let ruleId in clearanceRules) {
        if (ruleId != this.itemKey) {
          // Loop through other rules and remove this item from their clearances for any rules that don't appear in this item's clearances.
          let remoteRuleExistsOnThisItem = Path.get(itemData, `${clearanceType}.${ruleId}`) != null;
          let thisItemExistsOnRemoteRule = Path.get(clearanceRules[ruleId], `${clearanceType}.${this.itemKey}`) != null;
          if (!remoteRuleExistsOnThisItem && thisItemExistsOnRemoteRule) {
            update[Path.join([this.dataPath, `${ruleId}/${clearanceType}/${this.itemKey}`], '/')] = null;
          }
        }

        // Find the highest priority value
        let order = clearanceRules[ruleId].order;
        if (order > highestPriorityValue) highestPriorityValue = order;
      }

      // Loop through this item's clearances and update on all remote rules.
      let clearanceItems = Path.get(itemData, clearanceType);

      for (let ruleId in clearanceItems) {
        if (ruleId != this.itemKey) {
          let clearanceItem = clearanceItems[ruleId];
          // Check that the clearance item is not null (it could be if it was deleted).
          if (clearanceItem == null) continue;

          let mirrorCopy = Path.copy(clearanceItem);

          // Swap direction.
          if (mirrorCopy.direction == 'below') mirrorCopy.direction = 'above';
          else if (mirrorCopy.direction == 'above') mirrorCopy.direction = 'below';

          update[Path.join([this.dataPath, `${ruleId}/${clearanceType}/${this.itemKey}`], '/')] = mirrorCopy;
        }
      }
    }

    // Set the order of the new item if we are creating it
    if (this.creatingItem) itemData.order = highestPriorityValue + 1;

    await super._saveUpdate(itemData, update, detail);
  }

  labelChanged(label) {
    this.set('_itemData.shorthand', label);
  }

  removeException(e) {
    this.splice(`_itemData.exceptions`, e.model.index, 1);
  }

  showClearances() {
    return !this.creatingItem || this.finishedSteps;
  }

  updateItemDataMidspanClearances() {
    let itemDataMidspanClearances = this.get('_itemData.midspan_clearances');
    let itemDataMidspanClearancesExist = itemDataMidspanClearances != null && itemDataMidspanClearances != false;
    if (this.midspanClearances) {
      if (!itemDataMidspanClearancesExist) this.set('_itemData.midspan_clearances', this.backupMidspanClearances || {});
    } else {
      if (itemDataMidspanClearancesExist) this.backupMidspanClearances = itemDataMidspanClearances;
      this.set('_itemData.midspan_clearances', false);
    }
  }

  updateMidspanClearances() {
    let itemDataMidspanClearances = this.get('_itemData.midspan_clearances');
    this.midspanClearances = itemDataMidspanClearances != null && itemDataMidspanClearances != false;
  }

  updatedOtherAttributes() {
    let filterAttribute = [];
    let asAttrArray = Object.entries(this.otherAttributes ?? {});
    filterAttribute = asAttrArray.filter(([key, value]) => {
      if (Array.isArray(value?.attribute_types)) return value?.attribute_types.includes('photo');
      else return false;
    });
    this.filteredOtherAttr = Object.fromEntries(filterAttribute);
  }

  inputModelLabelFunction(item) {
    if (item?.name) return item.name;
    if (item?.$key) return CamelCase(item.$key);
    return '';
  }
}

customElements.define('katapult-clearance-editor', KatapultClearanceEditor);

class ClearanceTable extends KatapultElement {
  static get template() {
    return html`
      ${DefaultStyle}
      <style>
        :host {
          color: var(--primary-text-color-faded);
        }
      </style>
      <paper-table style="overflow: hidden;" rounded outline white>
        <paper-row grey divider>
          <paper-cell>Distance</paper-cell>
          <paper-cell>Direction</paper-cell>
          <paper-cell style="flex-grow: 2;">Clearance Rule</paper-cell>
          <paper-cell style="font-size: 8pt; text-align: center;" icon>Ignore Existing Violations</paper-cell>
          <paper-cell icon></paper-cell>
        </paper-row>
        <template is="dom-repeat" items="{{clearancesArray}}">
          <paper-row divider>
            <paper-cell>
              <paper-input type="number" no-label-float value="{{item.clearance}}">
                <span slot="suffix">in</span>
              </paper-input>
            </paper-cell>
            <paper-cell>
              <katapult-drop-down
                label="Select a Direction"
                items="[[getDirectionsList(item.$key, itemKey, directions.*)]]"
                label-path="label"
                value-path="value"
                value="{{item.direction}}"
                no-label-float
              ></katapult-drop-down>
            </paper-cell>
            <paper-cell style="flex-grow: 2;">
              <katapult-drop-down
                label="Select a Clearance Rule"
                items="[[getClearancesRulesList(item.$key, makeReadyItems, clearances.*)]]"
                label-path="label"
                value-path="value"
                value="{{item.$key}}"
                no-label-float
              >
                <paper-item on-click="createNewClearanceRule">CREATE NEW CLEARANCE RULE</paper-item>
              </katapult-drop-down>
            </paper-cell>
            <paper-cell icon checkbox>
              <paper-checkbox checked="{{item.ignore_existing_violations}}"></paper-checkbox>
            </paper-cell>
            <paper-cell icon-button>
              <katapult-button noBackground noBorder iconOnly icon="delete" on-click="removeClearance"></katapult-button>
            </paper-cell>
          </paper-row>
        </template>
        <paper-row ripple short on-click="addClearance">
          <paper-cell center pointer style="color: var(--paper-grey-600);">
            <span>ADD CLEARANCE</span>
            <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
          </paper-cell>
        </paper-row>
      </paper-table>
    `;
  }

  static get properties() {
    return {
      clearances: {
        type: Object,
        notify: true,
        value: {}
      },
      clearancesArray: {
        type: Array,
        value: []
      },
      directions: {
        type: Array,
        value: [
          { label: 'Above', value: 'above' },
          { label: 'Below', value: 'below' },
          { label: 'From', value: 'away from' }
        ]
      }
    };
  }

  static get observers() {
    return ['clearancesChanged(clearances.*)', 'clearancesArrayChanged(clearancesArray.*)'];
  }

  ready() {
    super.ready();
  }

  addClearance() {
    this.push('clearancesArray', {});
  }

  clearancesChanged(changeRecord) {
    if (this.clearances == null) {
      this.clearances = {};
    } else {
      if (changeRecord.path == 'clearances') {
        let temp = Path.copy(this.clearances);
        // Loop clearance items and make sure each one's clearance value is a string (paper-inputs will cast it to a string otherwise and that is costly as it generates data change events for each item).
        for (let key in temp) {
          let clearanceItem = temp[key];
          if (clearanceItem) {
            let stringClearance = String(clearanceItem.clearance);
            clearanceItem.clearance = stringClearance;
          }
        }
        // Keep only object by filtering out any items that have a $val property.
        this.clearancesArray = ToArray(temp).filter((x) => x.$val === undefined);
      }
    }
  }

  clearancesArrayChanged(changeRecord) {
    let pathParts = changeRecord.path.split('.');
    // Handle array items changing.
    let itemIndex = Number(pathParts[1]);
    let lazy = new LazySet(this);
    if (!isNaN(itemIndex)) {
      let item = this.get(`clearancesArray.${itemIndex}`);
      if (item && item.$key) {
        // If this clearance rule is for the clearance item we are editing and the direction is not away from, force direction to be away from.
        if (item.$key == this.itemKey) {
          if (item.direction != 'away from') {
            // Backup the direction so that we can restore it if the clearance item of this clearance rule should be changed to a different clearance item than the one we are editing.
            Path.set(item, '$backup.direction', item.direction);
            lazy.set(`clearancesArray.${itemIndex}.direction`, 'away from');
          }
        } else if (Path.get(item, '$backup.direction')) {
          // Restore the direction.
          lazy.set(`clearancesArray.${itemIndex}.direction`, item.$backup.direction);
          // And clear the backup direction so we don't act upon it again.
          item.$backup.direction = null;
        }
        let temp = {};
        for (let key in item) if (!key.startsWith('$')) temp[key] = item[key];
        // Cast clearance back to a number.
        temp.clearance = Number(temp.clearance);
        this.set(`clearances.${item.$key}`, temp);
      }
    }
    // Check for keys in clearances that are not in clearancesArray.
    for (let key in this.clearances) {
      if (!this.clearancesArray.find((item) => item.$key == key)) {
        this.set(`clearances.${key}`, null);
      }
    }
    if (lazy) lazy.notify();
  }

  createNewClearanceRule(e) {
    e.currentTarget.parentElement.close();
    document.dispatchEvent(new CustomEvent('create-new-clearance-rule'));
  }

  getClearancesRulesList(key) {
    let temp = [];
    if (this.makeReadyItems) {
      ToArray(Path.copy(this.makeReadyItems))
        .sort((a, b) => a.order - b.order)
        .forEach((rule) => {
          if (rule.$key == key || this.get(`clearances.${rule.$key}`) == null)
            temp.push({ label: CamelCase(rule.shorthand), value: rule.$key });
        });
    }
    return temp;
  }

  getDirectionsList(key) {
    let temp = [];
    if (Array.isArray(this.directions)) {
      temp = this.directions;
      // If the clearance item specified by this clearance rule is the same as the clearance item we are editing, only allow 'away from' as a direction option.
      if (key == this.itemKey) temp = temp.filter((x) => x.value == 'away from');
    }
    return temp;
  }

  removeClearance(e) {
    this.splice('clearancesArray', e.model.index, 1);
  }
}

customElements.define('clearance-table', ClearanceTable);
